/**
 * Branded MS Word (.docx) capital-pack exports — D2D Energy / Eagle VDR.
 * Also: seller/advisor bid letter (RedOaks Bid Instructions) — separate from LP packs.
 * Zero-dep WordprocessingML + store ZIP.
 */
import { downloadBytes, zipStore } from './ooxmlZip.js';
import { evaluateReleaseReadiness } from './releaseGate.js';
import {
  BID_PROCESS_DEFAULTS,
  bidProcessFromMeta,
  createBidProposal,
  validateBidProposal,
} from './bidProposal.js';
import { ILLUSTRATIVE_BUYER_OH_MONTHLY, redactIllustrativeOverhead, scrubIllustrativeDollars } from './buyerOverhead.js';

const NAVY = '0C1F2E';
const AMBER = 'E8A317';
const MUTED = '64748B';

function xmlEscape(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function usd(v) {
  return `$${Number(v || 0).toLocaleString()}`;
}

/** Buyer overhead categories table + summary paragraph — desk judgment, not seller fact. */
function buyerOverheadSection(freeze) {
  const oh = freeze?.assumptions?.buyerOverhead;
  const b = freeze?.results?.base;
  if (!oh) {
    return [h2('Buyer overhead (categories · judgment)'), p('No buyer overhead snapshot captured in this save.')];
  }
  return [
    h2('Buyer overhead (categories · judgment)'),
    p(oh.illustrationNote || 'Buyer G&A categories are desk judgment, not seller facts.', {
      size: 16,
      color: MUTED,
    }),
    table(
      ['Category', '$/mo', 'In CF'],
      (oh.categories || []).map((c) => [
        c.label,
        c.redacted || c.monthly == null ? 'illustrative, omitted' : `${usd(c.monthly)}/mo`,
        c.includeInCf ? 'Yes' : 'No',
      ]),
    ),
    p(
      `Total ${oh.monthlyTotal == null ? 'illustrative, omitted' : `${usd(oh.monthlyTotal)}/mo`} · Included in CF ${
        oh.monthlyIncluded == null ? 'illustrative, omitted' : `${usd(oh.monthlyIncluded)}/mo`
      } · PV impact ${
        b?.buyerOhPvDeltaMM != null ? `$${b.buyerOhPvDeltaMM.toFixed(2)}MM` : '—'
      } (IRR w/o buyer OH ${pct(b?.irrWithoutBuyerOh)})`,
      { size: 16, color: MUTED },
    ),
  ];
}

/** Liability schedule table — negative-PV / inactive wells, never floored to zero. */
function liabilityScheduleSection(freeze) {
  const l = freeze?.liabilitySchedule;
  if (!l || !l.rowCount) {
    return [
      h2('Liability schedule (negative PV / inactive — not floored)'),
      p('No negative-PV / inactive wells flagged in this save.'),
    ];
  }
  return [
    h2('Liability schedule (negative PV / inactive — not floored)'),
    p(
      `${l.rowCount} well(s) carrying negative oneline PV / inactive flags — retained in package totals (not floored to zero).`,
      { size: 16, color: MUTED },
    ),
    table(
      ['Lease', 'Oneline PV', 'Model asset PV', 'Plug/ARO share', 'Note'],
      l.rows.map((r) => [
        r.lease,
        usd(r.onelinePv),
        r.modelAssetPv != null ? usd(r.modelAssetPv) : '—',
        r.suggestedPlugAroShareMM != null ? `$${r.suggestedPlugAroShareMM.toFixed(3)}MM` : '—',
        r.regulatoryNote || '',
      ]),
    ),
    p(
      `Oneline negative total ${usd(l.onelineNegativeTotal)} · Model negative total ${usd(l.modelNegativeTotal)}`,
      { size: 16, color: MUTED },
    ),
  ];
}

function p(text, { bold = false, size = 20, color = '1E293B', center = false, spaceAfter = 80 } = {}) {
  const align = center ? '<w:jc w:val="center"/>' : '';
  const rPr = `<w:rPr>
      <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri"/>
      <w:sz w:val="${size}"/><w:szCs w:val="${size}"/>
      <w:color w:val="${color}"/>
      ${bold ? '<w:b/>' : ''}
    </w:rPr>`;
  return `<w:p>
    <w:pPr>${align}<w:spacing w:after="${spaceAfter}"/></w:pPr>
    <w:r>${rPr}<w:t xml:space="preserve">${xmlEscape(text)}</w:t></w:r>
  </w:p>`;
}

function h1(text) {
  return p(text, { bold: true, size: 32, color: NAVY, spaceAfter: 120 });
}

function h2(text) {
  return p(text, { bold: true, size: 24, color: NAVY, spaceAfter: 100 });
}

function brandBanner(subtitle) {
  return `
  <w:p>
    <w:pPr>
      <w:shd w:val="clear" w:color="auto" w:fill="${NAVY}"/>
      <w:spacing w:before="0" w:after="0"/>
      <w:jc w:val="center"/>
    </w:pPr>
    <w:r>
      <w:rPr>
        <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>
        <w:b/><w:sz w:val="28"/><w:color w:val="FFFFFF"/>
      </w:rPr>
      <w:t>D2D Energy · Eagle East TX VDR</w:t>
    </w:r>
  </w:p>
  <w:p>
    <w:pPr>
      <w:shd w:val="clear" w:color="auto" w:fill="${AMBER}"/>
      <w:spacing w:before="0" w:after="200"/>
      <w:jc w:val="center"/>
    </w:pPr>
    <w:r>
      <w:rPr>
        <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>
        <w:b/><w:sz w:val="18"/><w:color w:val="${NAVY}"/>
      </w:rPr>
      <w:t>${xmlEscape(subtitle)}</w:t>
    </w:r>
  </w:p>`;
}

function metaLine(freeze) {
  return [
    p(`Confidential: ${freeze.confidentiality}`, { size: 16, color: MUTED, spaceAfter: 40 }),
    p(`Scenario: ${freeze.label} · ${freeze.scenarioId}`, { size: 16, color: MUTED, spaceAfter: 40 }),
    p(
      `Hash ${freeze.contentHash} · Saved ${String(freeze.frozenAt || '').replace('T', ' ').slice(0, 16)} · DealPackage ${freeze.generatorVersion || '—'}`,
      { size: 16, color: MUTED, spaceAfter: 160 },
    ),
  ].join('');
}

function table(headers, rows) {
  const colCount = headers.length;
  const width = Math.floor(9000 / colCount);
  const grid = Array.from({ length: colCount }, () => `<w:gridCol w:w="${width}"/>`).join('');

  const headerRow = `<w:tr>${headers
    .map(
      (h) => `<w:tc>
      <w:tcPr>
        <w:tcW w:w="${width}" w:type="dxa"/>
        <w:shd w:val="clear" w:color="auto" w:fill="${NAVY}"/>
      </w:tcPr>
      <w:p><w:pPr><w:spacing w:after="40"/></w:pPr>
        <w:r><w:rPr><w:b/><w:color w:val="FFFFFF"/><w:sz w:val="16"/>
          <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/></w:rPr>
          <w:t>${xmlEscape(h)}</w:t></w:r></w:p>
    </w:tc>`,
    )
    .join('')}</w:tr>`;

  const body = rows
    .map((row, ri) => {
      const fill = ri % 2 === 0 ? 'F8FAFC' : 'FFFFFF';
      return `<w:tr>${row
        .map(
          (cell) => `<w:tc>
        <w:tcPr>
          <w:tcW w:w="${width}" w:type="dxa"/>
          <w:shd w:val="clear" w:color="auto" w:fill="${fill}"/>
        </w:tcPr>
        <w:p><w:pPr><w:spacing w:after="40"/></w:pPr>
          <w:r><w:rPr><w:sz w:val="16"/><w:color w:val="1E293B"/>
            <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/></w:rPr>
            <w:t>${xmlEscape(cell)}</w:t></w:r></w:p>
      </w:tc>`,
        )
        .join('')}</w:tr>`;
    })
    .join('');

  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="9000" w:type="dxa"/>
      <w:tblBorders>
        <w:top w:val="single" w:sz="4" w:color="${NAVY}"/>
        <w:left w:val="single" w:sz="4" w:color="${NAVY}"/>
        <w:bottom w:val="single" w:sz="4" w:color="${NAVY}"/>
        <w:right w:val="single" w:sz="4" w:color="${NAVY}"/>
        <w:insideH w:val="single" w:sz="4" w:color="E2E8F0"/>
        <w:insideV w:val="single" w:sz="4" w:color="E2E8F0"/>
      </w:tblBorders>
    </w:tblPr>
    <w:tblGrid>${grid}</w:tblGrid>
    ${headerRow}${body}
  </w:tbl>
  <w:p><w:pPr><w:spacing w:after="160"/></w:pPr></w:p>`;
}

function footer() {
  return p(
    'Prepared by Data2Dollars.ai · Circulate named saved scenarios only — desk knobs remain editable in-app.',
    { size: 14, color: MUTED, center: true, spaceAfter: 0 },
  );
}

function forBuyerExport(freeze) {
  if (!freeze?.assumptions) return freeze;
  const monthly = freeze.assumptions.overheadMonthly;
  const illustrative = Math.abs(Number(monthly) - ILLUSTRATIVE_BUYER_OH_MONTHLY) < 1;
  return {
    ...freeze,
    assumptions: {
      ...freeze.assumptions,
      buyerOverhead: redactIllustrativeOverhead(freeze.assumptions.buyerOverhead),
      overheadMonthly: illustrative ? null : monthly,
    },
    disclosures: Array.isArray(freeze.disclosures)
      ? freeze.disclosures.map((d) => scrubIllustrativeDollars(d))
      : freeze.disclosures,
  };
}

function onePagerBody(freeze) {
  freeze = forBuyerExport(freeze);
  const a = freeze.assumptions;
  const b = freeze.results.base;
  const s = freeze.results.stress;
  const scenarios = freeze.results.namedScenarios || [];
  const disclosures =
    freeze.disclosures ||
    evaluateReleaseReadiness(freeze.releaseReadiness, { asOf: freeze.asOf || '' }).disclosures;

  return [
    brandBanner('Capital one-pager (saved scenario)'),
    h1(freeze.label || 'Working case'),
    metaLine(freeze),
    h2('Material disclosures'),
    ...(disclosures.length
      ? disclosures.map((d) => p(d))
      : [p('No additional disclosed items beyond concentration and bid-bridge facts.')]),
    h2('Concentration (disclose first)'),
    p(
      `Largest well is ${b ? (b.boswellShare * 100).toFixed(0) : '—'}% model-basis share of modeled PDP PV. Concentration risk, not diversified PDP.`,
    ),
    h2('Bid bridge (locked)'),
    table(
      ['Item', 'Value', 'Class'],
      [
        ['Bid price', `$${a.bidPriceMM.toFixed(2)}MM`, 'Judgment'],
        [`Gross ${b?.pvLabel || 'PV'}`, `$${b?.pvMM.toFixed(2)}MM`, 'Model'],
        ['ARO / P&A', `$${a.aroMM.toFixed(2)}MM`, 'Judgment'],
        ['After ARO', `$${b?.bidBridgePvMM.toFixed(2)}MM`, 'Model'],
        ['NTM CF', `$${b?.ntmCfMM.toFixed(2)}MM`, 'Model'],
        ['IRR @ bid (base)', pct(b?.irrAnnual), 'Computed'],
        ['MOIC @ bid (before P&A)', b?.moic != null ? `${b.moic.toFixed(2)}×` : '—', 'Computed'],
        ['IRR @ bid (desk stress)', pct(s?.irrAnnual), 'Computed'],
      ],
    ),
    p(
      'Desk stress (locked): oil −$5/bbl and netback −$2.50/bbl, or gas −$0.50/MMBtu and netback −$0.15/Mcf; largest well −25%; buyer G&A in cash flow. Not the packaged named bear scenario.',
      { size: 16, color: MUTED },
    ),
    ...buyerOverheadSection(freeze),
    ...liabilityScheduleSection(freeze),
    h2('Locked assumptions'),
    table(
      ['Knob', 'Value'],
      [
        ['HH', `$${a.hh.toFixed(2)}`],
        ['Boswell vol adj', `${(a.boswellVolAdj * 100).toFixed(0)}%`],
        ['Netback adj', `$${a.netbackAdj.toFixed(2)}/Mcf`],
        ['Discount', `${(a.discountRate * 100).toFixed(0)}%`],
        ['Overhead in CF', `${a.overheadInCf ? 'Yes' : 'No'} (${a.overheadMonthly == null ? 'illustrative, omitted' : `${usd(a.overheadMonthly)}/mo`})`],
      ],
    ),
    h2('Seller reference (fact)'),
    p(
      `Seller ask ~$${freeze.seller.askMM}MM · Oneline PDP PV10 $${freeze.seller.refPdpPv10MM ?? '—'}MM · Total PV10 $${freeze.seller.refTotalPv10MM ?? '—'}MM`,
    ),
    h2('Named scenarios at bid'),
    table(
      ['Scenario', 'PV', 'After ARO', 'IRR'],
      scenarios.map((r) => [
        r.label,
        `$${r.pvMM.toFixed(2)}MM`,
        `$${r.bridgeMM.toFixed(2)}MM`,
        pct(r.irrAnnual),
      ]),
    ),
    h2('Note / approver'),
    p(`Note: ${freeze.note || 'None'}`),
    p(`Approver: ${freeze.approver || 'Pending'}`),
    footer(),
  ].join('');
}

function icMemoBody(freeze) {
  const a = freeze.assumptions;
  const b = freeze.results.base;
  const s = freeze.results.stress;
  const cal = freeze.calibration;
  const ms = freeze.midstream;
  const scenarios = freeze.results.namedScenarios || [];

  return [
    brandBanner('IC decision memo (saved scenario)'),
    h1(freeze.label || 'Working case'),
    metaLine(freeze),
    h2('Working recommendation (judgment)'),
    p(freeze.recommendation || 'Pending IC — set on Decision desk before save.'),
    p(`Note: ${freeze.note || 'None'} · Approver: ${freeze.approver || 'Pending'}`, {
      size: 16,
      color: MUTED,
    }),
    h2('Concentration (disclose first)'),
    p(
      `Largest well is ${b ? (b.boswellShare * 100).toFixed(0) : '—'}% model-basis share of modeled PDP PV.`,
    ),
    table(
      ['Item', 'Value', 'Class'],
      [
        ['Seller ask (working)', `$${freeze.seller.askMM}MM`, 'Fact / framing'],
        ['Oneline PDP PV10', `$${freeze.seller.refPdpPv10MM ?? '—'}MM`, 'Fact'],
        [`Model ${b?.pvLabel || 'PV'} (base)`, `$${b?.pvMM.toFixed(2)}MM`, 'Model'],
        ['After ARO', `$${b?.bidBridgePvMM.toFixed(2)}MM`, 'Model'],
        ['Bid price', `$${a.bidPriceMM.toFixed(2)}MM`, 'Judgment'],
        ['IRR @ bid (base)', pct(b?.irrAnnual), 'Computed'],
        ['IRR @ bid (desk stress)', pct(s?.irrAnnual), 'Computed'],
        ['MOIC @ bid (before P&A)', b?.moic != null ? `${b.moic.toFixed(2)}×` : '—', 'Computed'],
        ['NTM CF', `$${b?.ntmCfMM.toFixed(2)}MM`, 'Model'],
      ],
    ),
    p(
      'Desk stress: oil −$5/bbl and netback −$2.50/bbl, or gas −$0.50/MMBtu and netback −$0.15/Mcf; largest well −25%; buyer G&A in cash flow. Not the packaged named bear.',
      { size: 16, color: MUTED },
    ),
    ...buyerOverheadSection(freeze),
    ...liabilityScheduleSection(freeze),
    h2('Locked assumptions'),
    table(
      ['Knob', 'Value'],
      [
        ['HH', `$${a.hh.toFixed(2)}`],
        ['Boswell vol adj', `${(a.boswellVolAdj * 100).toFixed(0)}%`],
        ['Netback adj', `$${a.netbackAdj.toFixed(2)}/Mcf`],
        ['Discount', `${(a.discountRate * 100).toFixed(0)}%`],
        ['ARO / P&A', `$${a.aroMM.toFixed(2)}MM`],
        ['Overhead in CF', `${a.overheadInCf ? 'Yes' : 'No'} (${a.overheadMonthly == null ? 'illustrative, omitted' : `${usd(a.overheadMonthly)}/mo`})`],
      ],
    ),
    h2('Named scenarios at bid'),
    table(
      ['ID', 'Label', 'PV', 'After ARO', 'IRR'],
      scenarios.map((r) => [
        r.id,
        r.label,
        `$${r.pvMM.toFixed(2)}MM`,
        `$${r.bridgeMM.toFixed(2)}MM`,
        pct(r.irrAnnual),
      ]),
    ),
    h2('Model fidelity (cfScale)'),
    table(
      ['Check', 'Value'],
      [
        ['Model base PV10', `$${cal?.modelBasePv10MM ?? '—'}MM`],
        ['Seller PDP PV10', `$${cal?.refPdpPv10MM ?? '—'}MM`],
        ['Model − seller', `$${cal?.modelVsRefPdpMM ?? '—'}MM`],
        ['cfScale median', `${cal?.cfScaleMedian ?? '—'}`],
        ['cfScale Boswell', `${cal?.cfScaleBoswell ?? '—'}`],
        ['Outliers (|scale−1| > 0.3)', `${cal?.cfScaleOutliers ?? '—'}`],
      ],
    ),
    ...(freeze.boswellDca
      ? [
          h2('Boswell production / DCA (fact vs judgment)'),
          table(
            ['Metric', 'Value', 'Class'],
            [
              ['Trough', `${freeze.boswellDca.troughMcfpd ?? '—'} Mcf/d`, 'Fact'],
              ['Post-acid', `${freeze.boswellDca.postAcidMcfpd ?? '—'} Mcf/d`, 'Fact'],
              ['Fitted De', `${freeze.boswellDca.fittedDePct ?? '—'}%/yr (${freeze.boswellDca.fitSource || '—'})`, 'Model'],
              [
                'Hyperbolic b',
                freeze.boswellDca.hyperbolicB != null
                  ? `${freeze.boswellDca.hyperbolicB} (R² ${freeze.boswellDca.hyperbolicR2 ?? '—'})`
                  : '—',
                'Model',
              ],
              ['Holdout MAPE', freeze.boswellDca.holdoutMapePct != null ? `${freeze.boswellDca.holdoutMapePct}%` : '—', 'Model'],
              [
                'Locked boswellVolAdj',
                freeze.boswellDca.volAdj != null ? `${(freeze.boswellDca.volAdj * 100).toFixed(0)}%` : '—',
                'Judgment',
              ],
              ['Fit-ready', freeze.boswellDca.fitReady == null ? '—' : freeze.boswellDca.fitReady ? 'Yes' : 'NO', 'Model'],
            ],
          ),
          p(
            `boswellVolAdj is the only volume judgment; DCA never re-tunes cfScale.${freeze.boswellDca.shortWindow ? ' A post-acid window < 90d cannot alone prove durability.' : ''}`,
            { size: 16, color: MUTED },
          ),
        ]
      : []),
    h2('Midstream assignability'),
    p(
      ms
        ? `Status: ${(ms.overallStatus || 'unknown').toUpperCase()} · ${ms.openCount ?? '—'}/${ms.contractCount ?? '—'} contracts open · L12M realized $${ms.l12Realized ?? '—'}/Mcf · stack ~$${ms.stackAt6k ?? '—'} @ 6k MMBtu/d.`
        : 'Not captured in this save — see Sensitivity / Diligence register.',
    ),
    h2('Multiple back-check (not the bid frame)'),
    p(
      `4× $${b?.multiples.x4.toFixed(1)}MM · 5× $${b?.multiples.x5.toFixed(1)}MM · 6× $${b?.multiples.x6.toFixed(1)}MM on NTM CF.`,
    ),
    footer(),
  ].join('');
}

function buildDocx(bodyXml) {
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    ${bodyXml}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="720" w:right="720" w:bottom="720" w:left="720"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`;

  const docRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`;

  const now = new Date().toISOString();
  const core = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
  xmlns:dc="http://purl.org/dc/elements/1.1/"
  xmlns:dcterms="http://purl.org/dc/terms/"
  xmlns:dcmitype="http://purl.org/dc/dcmitype/"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Eagle East TX — D2D Energy capital pack</dc:title>
  <dc:creator>Data2Dollars.ai</dc:creator>
  <cp:lastModifiedBy>D2D Energy VDR</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`;

  const app = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"
  xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>D2D Energy Eagle VDR</Application>
  <Company>Data2Dollars.ai</Company>
</Properties>`;

  return zipStore([
    { name: '[Content_Types].xml', data: contentTypes },
    { name: '_rels/.rels', data: rootRels },
    { name: 'word/document.xml', data: document },
    { name: 'word/_rels/document.xml.rels', data: docRels },
    { name: 'docProps/core.xml', data: core },
    { name: 'docProps/app.xml', data: app },
  ]);
}

export function buildOnePagerDocx(freeze) {
  return buildDocx(onePagerBody(freeze));
}

export function buildIcMemoDocx(freeze) {
  return buildDocx(icMemoBody(freeze));
}

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function downloadOnePagerDocx(freeze) {
  downloadBytes(`${freeze.scenarioId}-one-pager.docx`, buildOnePagerDocx(freeze), DOCX_MIME);
}

export function downloadIcMemoDocx(freeze) {
  downloadBytes(`${freeze.scenarioId}-ic-memo.docx`, buildIcMemoDocx(freeze), DOCX_MIME);
}

/** Investor-portal one-pager — uses sanitized projection only (no seller/calibration). */
function investorOnePagerBody(freeze) {
  const a = freeze?.assumptions || {};
  const b = freeze?.results?.base || {};
  const s = freeze?.results?.stress || {};
  const scenarios = freeze?.results?.namedScenarios || [];
  const n = (v, d = 2) => (v == null || Number.isNaN(+v) ? '—' : Number(v).toFixed(d));
  const gate = evaluateReleaseReadiness(freeze?.releaseReadiness, { asOf: freeze?.asOf || '' });
  if (!gate.ready) {
    throw new Error(
      `External one-pager blocked by release readiness: ${gate.blockers.join(', ') || 'incomplete'}`,
    );
  }
  return [
    brandBanner('Illustrative asset-underwriting one-pager'),
    h1(freeze?.label || 'Working case'),
    p(`Confidential: ${freeze?.confidentiality || 'Firebrand Energy — investor materials'}`, {
      size: 16,
      color: MUTED,
      spaceAfter: 40,
    }),
    p(
      `Hash ${freeze?.contentHash || '—'} · ${String(freeze?.approvedAt || freeze?.frozenAt || '').replace('T', ' ').slice(0, 16) || '—'}`,
      { size: 16, color: MUTED, spaceAfter: 160 },
    ),
    h2('Illustrative asset-underwriting snapshot'),
    table(
      ['Item', 'Value'],
      [
        ['Illustrative asset bid', `$${n(a.bidPriceMM)}MM`],
        [`Gross ${b.pvLabel || 'PV'}`, `$${n(b.pvMM)}MM`],
        ['After ARO', b.bidBridgePvMM != null ? `$${n(b.bidBridgePvMM)}MM` : '—'],
        ['NTM CF', `$${n(b.ntmCfMM)}MM`],
        ['Illustrative asset-model IRR @ bid (base)', pct(b.irrAnnual)],
        ['Illustrative asset-model MOIC @ bid', b.moic != null ? `${n(b.moic)}×` : '—'],
        ['Illustrative asset-model IRR @ bid (stress)', pct(s.irrAnnual)],
        ['HH', a.hh != null ? `$${n(a.hh)}` : '—'],
        ['Discount', a.discountRate != null ? pct(a.discountRate) : '—'],
      ],
    ),
    (freeze?.disclosures || []).length
      ? [
          h2('Material disclosures'),
          ...freeze.disclosures.map((disclosure) => p(disclosure)),
        ].join('')
      : '',
    scenarios.length
      ? [
          h2('Named scenarios'),
          table(
            ['Scenario', 'PV', 'IRR'],
            scenarios.map((r) => [r.label || '—', `$${n(r.pvMM)}MM`, pct(r.irrAnnual)]),
          ),
        ].join('')
      : '',
    p(
      'Illustrative asset-underwriting metrics only. This material is not a financing commitment or investor-return projection.',
      { size: 16, color: MUTED, spaceBefore: 120, spaceAfter: 100 },
    ),
    footer(),
  ].join('');
}

export function downloadInvestorOnePagerDocx(freeze) {
  const id = freeze?.scenarioId || freeze?.contentHash || 'investor';
  downloadBytes(`${id}-investor-one-pager.docx`, buildDocx(investorOnePagerBody(freeze)), DOCX_MIME);
}

function formatUsdMm(v) {
  if (v == null || Number.isNaN(+v)) return '—';
  return `$${Number(v).toFixed(2)} million`;
}

function formatLongDate(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Seller/advisor bid letter body — no IRR, LP teaser, or model internals. */
function bidLetterBody(freeze, proposalInput) {
  const process = bidProcessFromMeta({
    ...(freeze?.meta || {}),
    bidProcess: freeze?.bidProcess,
    effectiveDate: freeze?.bidProcess?.effectiveDate || freeze?.meta?.effectiveDate,
    bidsDue: freeze?.bidProcess?.bidsDue || freeze?.meta?.bidsDue,
  });
  const priceMM =
    proposalInput?.purchasePriceMM ??
    freeze?.proposal?.purchasePriceMM ??
    freeze?.assumptions?.bidPriceMM;
  const proposal = createBidProposal({
    meta: { caParty: proposalInput?.letterheadEntity, effectiveDate: process.effectiveDate },
    prior: { ...(freeze?.proposal || {}), ...(proposalInput || {}), purchasePriceMM: priceMM },
    bidPriceMM: priceMM,
  });
  const gate = validateBidProposal(proposal);
  if (!gate.ok) {
    throw new Error(`Bid letter incomplete: ${gate.blockers.join('; ')}`);
  }
  const prop = gate.proposal;
  const to = process.submitTo || BID_PROCESS_DEFAULTS.submitTo;
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const contingencyLines = (prop.contingencies || []).filter(Boolean);
  const contactLines = prop.contacts.filter((c) => c.name);

  return [
    p(prop.letterheadEntity, { bold: true, size: 28, color: NAVY, spaceAfter: 40 }),
    p('CONFIDENTIAL — BID PROPOSAL', { bold: true, size: 18, color: MUTED, spaceAfter: 160 }),
    p(today, { size: 20, spaceAfter: 160 }),
    p(`${to.name} | ${to.title}`, { size: 20, spaceAfter: 40 }),
    p(to.firm || BID_PROCESS_DEFAULTS.submitTo.firm, { size: 20, spaceAfter: 40 }),
    p(`Email: ${to.email}`, { size: 20, spaceAfter: 40 }),
    p(`Phone: ${to.phone || ''}`, { size: 20, spaceAfter: 160 }),
    p(`Re: ${process.packageName || BID_PROCESS_DEFAULTS.packageName}`, {
      bold: true,
      size: 20,
      spaceAfter: 160,
    }),
    p(`Dear ${to.name},`, { size: 20, spaceAfter: 120 }),
    p(
      `${prop.letterheadEntity} is pleased to submit this written proposal to acquire the operated properties offered by ETXENERGY, LLC (“Eagle”) in East Texas, as presented by RedOaks Energy Advisors, LLC.`,
      { size: 20, spaceAfter: 120 },
    ),
    p(
      `Proposed purchase price: ${formatUsdMm(prop.purchasePriceMM)} (USD), subject to the terms and contingencies described below.`,
      { size: 20, spaceAfter: 160 },
    ),
    h2('I. Effective Date'),
    p(
      `The proposed effective date for the transaction is ${formatLongDate(prop.effectiveDate)}.`,
      { size: 20, spaceAfter: 160 },
    ),
    h2('II. Financing and Other Contingencies'),
    p(
      prop.financingNarrative ||
        'Please see financing sources, structure, and certainty below.',
      { size: 20, spaceAfter: 100 },
    ),
    p(`Financing sources: ${prop.financingSources || '—'}`, { size: 20, spaceAfter: 80 }),
    p(`Expected structure: ${prop.financingStructure || '—'}`, { size: 20, spaceAfter: 80 }),
    p(`Certainty / availability through closing: ${prop.financingCertainty || '—'}`, {
      size: 20,
      spaceAfter: 100,
    }),
    contingencyLines.length
      ? [
          p('Other contingencies:', { bold: true, size: 20, spaceAfter: 80 }),
          ...contingencyLines.map((c) => p(`• ${c}`, { size: 20, spaceAfter: 60 })),
        ].join('')
      : '',
    p('', { size: 10, spaceAfter: 80 }),
    h2('III. Timeline / Proposal Validity'),
    p(
      `This proposal shall remain in effect until at least ${formatLongDate(prop.validThrough)}, to allow RedOaks and the Company a thorough evaluation. We understand you may contact us for clarification of terms.`,
      { size: 20, spaceAfter: 160 },
    ),
    h2('IV. Contact'),
    p(
      'The following person(s) will be available to answer questions and offer clarification regarding this proposal:',
      { size: 20, spaceAfter: 100 },
    ),
    table(
      ['Name', 'Role', 'Email', 'Office', 'Mobile'],
      contactLines.map((c) => [c.name, c.role || '—', c.email || '—', c.office || '—', c.mobile || '—']),
    ),
    p('We appreciate your consideration and look forward to discussing this proposal.', {
      size: 20,
      spaceAfter: 160,
    }),
    p('Regards,', { size: 20, spaceAfter: 200 }),
    p(prop.letterheadEntity, { bold: true, size: 20, spaceAfter: 40 }),
  ].join('');
}

export function buildBidLetterDocx(freeze, proposal) {
  return buildDocx(bidLetterBody(freeze, proposal));
}

export function downloadBidLetterDocx(freeze, proposal) {
  const id = freeze?.scenarioId || freeze?.contentHash || 'bid';
  const entity = (proposal?.letterheadEntity || freeze?.proposal?.letterheadEntity || 'bidder')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  downloadBytes(`${id}-${entity || 'bidder'}-bid-letter.docx`, buildBidLetterDocx(freeze, proposal), DOCX_MIME);
}

/** Low-level builders reused by the asset-workflow DCA/EUR memo (src/assets/workflowExport.js). */
export const docxParts = { p, h1, h2, brandBanner, table, footer, buildDocx, DOCX_MIME };
